import { Link } from 'react-router-dom';
import {
  UXP_UC_ID,
  COMMAND_CENTER_METRICS,
  KERNEL_ENTITIES,
  MARKETPLACE_CONNECTORS,
  ADAPTER_FEATURES,
  MARKETPLACE_RULES,
  CHANNEL_MAPPING_TYPES,
  UNIFIED_OPERATIONS,
  LOCALIZATION_FEATURES,
  ADVISOR_CAPABILITIES,
  COMMERCE_ANALYTICS,
  COLLABORATION_FEATURES,
  ACCESSIBILITY_STANDARDS,
  ARCHITECTURE_STAGES,
  LIVE_ROUTES,
  DEMO_KPIS,
  DEMO_KERNEL,
  DEMO_CONNECTORS,
  DEMO_ADAPTERS,
  DEMO_RULES,
  DEMO_MAPPINGS,
  DEMO_UNIFIED_OPS,
  DEMO_ADVISOR,
  buildUnifiedCommerceDashboard,
} from '../../unifiedcommerce/framework';
import {
  UnifiedCommerceCommandCenterPanel,
  CommerceKernelPanel,
  MarketplaceConnectorsPanel,
  MarketplaceAdapterPanel,
  MarketplaceRulesPanel,
  ChannelMappingPanel,
  UnifiedOperationsPanel,
  AIMarketplaceAdvisorPanel,
} from '../../components/unifiedcommerce';
import { Card, KpiCard } from '../../components/ui';

export function UnifiedCommercePage() {
  const dashboard = buildUnifiedCommerceDashboard();

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">{UXP_UC_ID}</p>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Unified Commerce Platform</h1>
        <p className="mt-1 text-sm text-slate-500">
          Multi-Marketplace Commerce Platform, Channel Expansion & Unified Commerce Operating System —
          marketplace abstraction layer with canonical commerce kernel and adapter framework.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(LIVE_ROUTES).map(([key, path]) => (
            <Link
              key={key}
              to={path}
              className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-100 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300"
            >
              {path}
            </Link>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Command Center Metrics" value={dashboard.command_center_metrics.toString()} />
        <KpiCard label="Kernel Entities" value={dashboard.kernel_entities.toString()} />
        <KpiCard label="Marketplace Connectors" value={dashboard.marketplace_connectors.toString()} />
        <KpiCard label="Unified Operations" value={dashboard.unified_operations.toString()} />
      </div>

      <section>
        <h2 className="text-lg font-semibold">Architecture Pipeline</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {ARCHITECTURE_STAGES.map((s, i) => (
            <span key={s} className="flex items-center gap-1 text-xs">
              <span className="rounded-lg border border-slate-200 px-2 py-1 dark:border-slate-700">{s}</span>
              {i < ARCHITECTURE_STAGES.length - 1 && <span className="text-slate-400">→</span>}
            </span>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Unified Commerce Command Center</h2>
        <p className="mt-1 text-sm text-slate-500">{COMMAND_CENTER_METRICS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <UnifiedCommerceCommandCenterPanel kpis={DEMO_KPIS} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Commerce Kernel</h2>
        <p className="mt-1 text-sm text-slate-500">{KERNEL_ENTITIES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <CommerceKernelPanel entities={DEMO_KERNEL} />
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Marketplace Connectors</h2>
          <p className="mt-1 text-sm text-slate-500">{MARKETPLACE_CONNECTORS.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <MarketplaceConnectorsPanel connectors={DEMO_CONNECTORS} />
          </Card>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Marketplace Adapter Framework</h2>
          <p className="mt-1 text-sm text-slate-500">{ADAPTER_FEATURES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <MarketplaceAdapterPanel adapters={DEMO_ADAPTERS} />
          </Card>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Marketplace Rules</h2>
          <p className="mt-1 text-sm text-slate-500">{MARKETPLACE_RULES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <MarketplaceRulesPanel rules={DEMO_RULES} />
          </Card>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Channel Mapping</h2>
          <p className="mt-1 text-sm text-slate-500">{CHANNEL_MAPPING_TYPES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <ChannelMappingPanel mappings={DEMO_MAPPINGS} />
          </Card>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Unified Operations</h2>
        <p className="mt-1 text-sm text-slate-500">{UNIFIED_OPERATIONS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <UnifiedOperationsPanel operations={DEMO_UNIFIED_OPS} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">AI Marketplace Advisor</h2>
        <p className="mt-1 text-sm text-slate-500">{ADVISOR_CAPABILITIES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <AIMarketplaceAdvisorPanel recommendations={DEMO_ADVISOR} />
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Localization</h2>
          <p className="mt-1 text-sm text-slate-500">{LOCALIZATION_FEATURES.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Commerce Analytics</h2>
          <p className="mt-1 text-sm text-slate-500">{COMMERCE_ANALYTICS.join(' · ')}</p>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Collaboration</h2>
          <p className="mt-2 text-xs text-slate-500">{COLLABORATION_FEATURES.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Accessibility</h2>
          <div className="mt-2 flex flex-wrap gap-1">
            {ACCESSIBILITY_STANDARDS.map((s) => (
              <span key={s} className="rounded border border-slate-200 px-2 py-0.5 text-xs dark:border-slate-700">{s}</span>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
